import React from 'react'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import { Box, Theme } from '@material-ui/core/'
import { getBrandIdentity } from '@src/utils/getBrandIdentity'
import HandleMarkup from '@src/components/HandleMarkup'
import { AemFooter, AemFooterLinksItem } from '@src/types/graphql-types'
import { sendNavigationInteractionEvent } from '@src/utils/analytics'
import { FooterLinks } from '@utils/analytics/enums'
import getConfig from 'next/config'
import { Link } from '@src/components/Link'
import CookieFooterLink from '@src/components/CookieFooterLink'
import { getTarget } from '@sial/aem-cms-components/dist/_utils/links'
import RichText from '@sial/aem-cms-components/dist/RichText'
import { useCheckUserCountry } from '@src/utils/useCheckUserCountry'
import countriesMap from '@localization/countries.json'
import { useRouter } from '@src/routes'
import { parseRegionalUrl } from '@src/utils/regional'

const {
  publicRuntimeConfig: { cookieConsent, cookieDataDomainScript },
} = getConfig()

const brandIdentity = getBrandIdentity()

const useStyles = makeStyles((theme: Theme) => {
  return {
    footer: {
      width: '100%',
      padding: theme.spacing(5, 0),
      background:
        brandIdentity === 'SigmaAldrich'
          ? theme.palette.background.grey
          : theme.palette.background.blue,
      [theme.breakpoints.up('md')]: {
        padding: theme.spacing(10, 0),
      },
    },
    wrapper: {
      maxWidth: 1320,
      margin: '0 auto',
      padding: theme.spacing(0, 5),
    },
    main: {
      display: 'none',
      marginBottom: 50,
      [theme.breakpoints.up('md')]: {
        display: 'flex',
      },
    },
    mainSpacer: {
      flexGrow: 1,
      height: 200,
      borderRight: '1px solid rgba(0, 0, 0, 0.26)',
    },
    navigationLeftSection: {
      width: '100%',
      maxWidth: 200,
      marginRight: 20,
      '& > span': {
        display: 'block',
        fontWeight: theme.typography.fontWeightBold,
        fontSize: theme.typography.pxToRem(16),
        margin: 0,
      },
    },
    navigationLeftLink: {
      display: 'block',
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.typography.pxToRem(14),
      lineHeight: 0.93,
      marginTop: '20px !important',
    },
    socialIconContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      margin: '7px -7px 8px 0',
    },
    socialIcon: {
      margin: '8px 8px 7px 0',
      flexShrink: 0,
    },
    navigationRightSection: {
      width: '100%',
      maxWidth: '330px',
      marginLeft: '68px',
      '& > span': {
        display: 'block',
        fontWeight: 900,
        fontSize: 16,
        marginBottom: 8,
      },
    },
    portfolio: {
      borderTop: '2px solid rgba(0, 0, 0, 0.10)',
      borderBottom: '2px solid rgba(0, 0, 0, 0.10)',
      minHeight: 80,
      display: 'none',
      alignItems: 'center',
      [theme.breakpoints.up('md')]: {
        display: 'flex',
      },
    },
    brandLogo: {
      maxWidth: 75,
      maxHeight: 75,
      flexShrink: 0,
    },
    portfolioLink: {
      marginLeft: '75px !important',
      fontSize: theme.typography.pxToRem(16),
      fontWeight: theme.typography.fontWeightBold,
      lineHeight: 1.06,
      color: '#000 !important',
    },
    legal: {
      fontSize: theme.typography.pxToRem(12),
      marginTop: 0,
      [theme.breakpoints.up('md')]: {
        marginTop: theme.spacing(8),
      },
      '& p': {
        margin: 0,
      },
    },
    trademarkHeadline: {
      fontWeight: theme.typography.fontWeightBold,
    },
    trademarkSubhead: {
      display: 'block',
      [theme.breakpoints.up('md')]: {
        display: 'flex',
      },
    },
    trademarkSubheadText: {
      marginRight: theme.spacing(0),
      [theme.breakpoints.up('md')]: {
        marginRight: theme.spacing(2),
      },
    },
    trademarkSubheadLinks: {
      marginTop: theme.spacing(1 / 2),
      [theme.breakpoints.up('md')]: {
        marginTop: theme.spacing(0),
      },
    },
    legalLink: {
      fontWeight: theme.typography.fontWeightBold,
    },
    legalLinkSpacer: {
      padding: theme.spacing(0, 2),
      color: theme.palette.grey[400],
    },
    socialIconImage: {
      display: 'block',
      marginBottom: 0,
    },
    brandLogoImage: {
      display: 'block',
      marginBottom: 0,
    },
  }
})

interface LinkWrapperProps extends AemFooterLinksItem {
  className?: string
  title?: string
}

const LinkWrapper: React.FC<LinkWrapperProps> = (props) => {
  const router = useRouter()
  const { linkLabel, className, title } = props
  let { linkUrl } = props
  const linkProps = getTarget(linkUrl)
  const {
    basename: { country, language },
  } = parseRegionalUrl(router.asPath)
  const {
    basename: { country: navCountry },
    pathAfterBasename,
  } = parseRegionalUrl(linkUrl || undefined)
  if (navCountry && country !== navCountry && pathAfterBasename)
    linkUrl = `/${country}/${language}${pathAfterBasename}`

  const footerEvent = () => {
    sendNavigationInteractionEvent({
      action: 'footer click',
      detail: `${title} > ${linkLabel}`,
      component: 'footer',
      elementType: 'link',
      elementText: linkLabel,
      linkUrl,
    })
  }
  return (
    <Link href={linkUrl || ''} passHref>
      <a
        className={className}
        role="link"
        tabIndex={0}
        {...linkProps}
        onClick={() => footerEvent()}
        onKeyPress={() => footerEvent()}
      >
        <HandleMarkup value={linkLabel} />
      </a>
    </Link>
  )
}

const GlobalFooter: React.FC<AemFooter> = (props) => {
  const {
    footerLeftContent,
    footerRightContent,
    portfolioLinks,
    brandLogo,
    legalLinks,
    trademarkBoldText,
    trademarkSmallText,
    socialContent,
  } = props

  const theme = useTheme()

  const classes = useStyles()
  const isVNUser = useCheckUserCountry(countriesMap.VN.countryCode)

  return (
    <footer id="footer" className={classes.footer}>
      <div className={classes.wrapper}>
        <div className={classes.main}>
          {footerLeftContent &&
            footerLeftContent.map((content, contentIndex) => (
              <div key={contentIndex} className={classes.navigationLeftSection}>
                {content?.title && <HandleMarkup value={content.title} />}
                {content?.items &&
                  content.items.map((item, itemIndex) => (
                    <LinkWrapper
                      key={itemIndex}
                      {...item}
                      className={classes.navigationLeftLink}
                      title={content?.title || ''}
                    />
                  ))}
              </div>
            ))}
          {socialContent &&
            socialContent.map((content, index) => (
              <div key={index} className={classes.navigationLeftSection}>
                {content?.title && <HandleMarkup value={content.title} />}
                {content?.contentType === 'social-links' &&
                  content.socialLinks &&
                  content.socialLinks.map((item: any, itemIndex) => (
                    <LinkWrapper
                      key={itemIndex}
                      {...item}
                      className={classes.navigationLeftLink}
                      title={content?.title || ''}
                    />
                  ))}
                {content?.contentType === 'social-icons' && (
                  <div className={classes.socialIconContainer}>
                    {content.items &&
                      content.items.map((item, itemIndex) => (
                        <div key={itemIndex} className={classes.socialIcon}>
                          <a
                            href={item?.linkUrl || ''}
                            {...(item?.linkUrl ? getTarget(item.linkUrl) : '')}
                            onClick={() => {
                              const linkText = item?.title?.split(' ')[0]
                              sendNavigationInteractionEvent({
                                action: 'footer click',
                                detail: `social media > ${linkText}`,
                                component: 'footer',
                                elementType: 'icon',
                                elementText: linkText,
                                linkUrl: item?.linkUrl,
                              })
                            }}
                          >
                            <img
                              src={item?.src || ''}
                              alt={item?.alt || ''}
                              className={classes.socialIconImage}
                              width="32"
                              height="32"
                            />
                          </a>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ))}
          <div className={classes.mainSpacer}></div>
          {footerRightContent &&
            footerRightContent.map((content: any, index) => (
              <div key={index} className={classes.navigationRightSection}>
                {content?.title && <HandleMarkup value={content.title} />}
                {content?.contentType === 'rteText' && content.rightText && (
                  <div>
                    <RichText text={content.rightText} />
                  </div>
                )}
                {content?.contentType === 'Img' && content.image && (
                  <img
                    {...content.image}
                    src={content.image}
                    alt={content.title || ''}
                  />
                )}
              </div>
            ))}
        </div>
        <div className={classes.portfolio}>
          {brandLogo && (
            <div className={classes.brandLogo}>
              <a
                href={brandLogo?.linkUrl || ''}
                {...(brandLogo?.linkUrl ? getTarget(brandLogo.linkUrl) : '')}
                onClick={() => {
                  sendNavigationInteractionEvent({
                    action: 'footer click',
                    detail: 'home',
                    component: 'footer',
                    elementType: 'link',
                    elementText: 'm',
                    linkUrl: brandLogo.linkUrl,
                  })
                }}
              >
                <img
                  src={brandLogo?.src || ''}
                  alt={brandLogo?.alt || ''}
                  className={classes.brandLogoImage}
                  width="75"
                  height="53"
                />
              </a>
            </div>
          )}
          {portfolioLinks &&
            portfolioLinks.map((link, linkIndex) => (
              <LinkWrapper
                key={linkIndex}
                {...link}
                className={classes.portfolioLink}
                title={FooterLinks.ExternalLinks}
              />
            ))}
        </div>
        <div className={classes.legal}>
          {trademarkBoldText && (
            <div className={classes.trademarkHeadline}>
              <RichText text={trademarkBoldText} />
            </div>
          )}
          {trademarkSmallText || (legalLinks && legalLinks.length > 0) ? (
            <div className={classes.trademarkSubhead}>
              {trademarkSmallText && (
                <div className={classes.trademarkSubheadText}>
                  <RichText text={trademarkSmallText} />
                </div>
              )}
              <div className={classes.trademarkSubheadLinks}>
                {legalLinks &&
                  legalLinks.map((link, linkIndex) => (
                    <React.Fragment key={linkIndex}>
                      <LinkWrapper
                        {...link}
                        className={classes.legalLink}
                        title={FooterLinks.TermsAndConditions}
                      />
                      {linkIndex !== legalLinks.length - 1 ? (
                        <span className={classes.legalLinkSpacer}>|</span>
                      ) : (
                        <>
                          {cookieConsent && cookieDataDomainScript && (
                            <CookieFooterLink legalLinks />
                          )}
                        </>
                      )}
                    </React.Fragment>
                  ))}
                {cookieConsent &&
                  cookieDataDomainScript &&
                  (!legalLinks || !legalLinks.length) && (
                    <CookieFooterLink legalLinks={false} />
                  )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {isVNUser && (
        <Box mt={10} bgcolor={theme.palette.primary.light} pt={5} pb={10}>
          <Box
            display="flex"
            justifyContent="space-between"
            className={classes.wrapper}
          >
            <div>
              <Box fontWeight="fontWeightMedium">
                CÔNG TY TNHH MERCK VIỆT NAM
              </Box>
              <p>
                Trụ sở chính: Tòa nhà CentrePoint, Số 106, Đường Nguyễn Văn
                Trỗi, Phường 08, Quận Phú Nhuận, Thành phố Hồ Chí Minh, Việt Nam
              </p>
              <p>Mã số doanh nghiệp: 0309785593</p>
              <p>
                Cấp bởi Sở kế hoạch và Đầu tư Thành Phố Hồ Chí Minh - Phòng đăng
                ký kinh doanh
              </p>
              <p>Đăng ký lần đầu: ngày 19 tháng 01 năm 2010</p>
              <p>Đăng ký thay đổi lần thứ: 12, ngày 10 tháng 11 năm 2023</p>
              <p>Điện thoại: 02838420100</p>
            </div>
            <div>
              <a
                href="http://online.gov.vn/Home/WebDetails/95755"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/static/VN_footer_logo.png"
                  alt="VN Footer Logo"
                  width="200px"
                  height="75px"
                ></img>
              </a>
            </div>
          </Box>
        </Box>
      )}
    </footer>
  )
}

export default GlobalFooter
